import uniqBy from 'lodash/uniqBy';
import { createHook, createSelector, type BoundActions } from 'react-sweet-state';
import type { State, MappingState } from '../../../../common/types';
import type { Actions } from '../../actions';
import { Store } from '../../main';

export type UseCSVReviewData = () => [CSVReviewDataState, Partial<BoundActions<State, Actions>>];

export type CSVReviewDataState = {
	numOfImportedFields: number;
	numOfTotalFields: number;
	numOfExistingFields: number;
	numOfCustomFields: number;
	numOfImportedUsers: number;
	numOfUniqueImportedUsers: number;
	numOfTotalUsers: number;
	numOfIssue: string | null;
};

export const csvReviewDataSelector = createSelector(
	(state: State): MappingState | null => state.mappingState,
	(newState: MappingState | null) => {
		if (newState === null) {
			return {
				numOfImportedFields: 0,
				numOfTotalFields: 0,
				numOfExistingFields: 0,
				numOfCustomFields: 0,
				numOfImportedUsers: 0,
				numOfUniqueImportedUsers: 0,
				numOfTotalUsers: 0,
				numOfIssue: null,
			};
		}

		const numOfTotalFields = newState.columnOrder.length;
		const numOfExistingFields = newState.columnOrder.filter(
			(colName) => newState.columns[colName].field?.fieldId,
		).length;
		const numOfCustomFields = newState.columnOrder.filter(
			(colName) => newState.columns[colName].field && !newState.columns[colName].field?.fieldId,
		).length;
		const numOfTotalUsers =
			newState.shouldImportUsers && newState.userMapping
				? Object.keys(newState.userMapping).length
				: 0;

		const numOfImportedUsers =
			newState.shouldImportUsers && newState.userMapping
				? Object.values(newState.userMapping).filter((user) => user.userValue).length
				: 0;
		const numOfUniqueImportedUsers =
			newState.shouldImportUsers && newState.userMapping
				? uniqBy(Object.values(newState.userMapping), 'userValue').filter((user) => user.userValue)
						.length
				: 0;

		return {
			numOfImportedFields: numOfExistingFields + numOfCustomFields,
			numOfTotalFields,
			numOfExistingFields,
			numOfCustomFields,
			numOfImportedUsers,
			numOfUniqueImportedUsers,
			numOfTotalUsers,
			numOfIssue: newState.issueCount,
		};
	},
);

export const useCSVReviewData: UseCSVReviewData = createHook(Store, {
	selector: csvReviewDataSelector,
});
